/**
 * RouteGuard — wraps a route element and enforces a {@link RouteRequirement}
 * using the PURE {@link guard} decision. Redirects (via react-router `Navigate`)
 * when denied; renders a loading placeholder while auth resolves.
 */
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../services/context.js';
import { guard, type RouteRequirement } from '../auth/model.js';
import { safeGameReturnPath } from '../auth/gameReturn.js';

export interface RouteGuardProps {
  requirement: RouteRequirement;
  children: ReactNode;
}

export function RouteGuard({ requirement, children }: RouteGuardProps) {
  const { loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="empty">Checking access…</div>;
  }

  const verdict = guard(user, requirement);
  if (!verdict.allow) {
    const returnTo = safeGameReturnPath(`${location.pathname}${location.search}${location.hash}`);
    const destination = verdict.redirectTo === '/login' && returnTo
      ? `/login?returnTo=${encodeURIComponent(returnTo)}`
      : verdict.redirectTo;
    return <Navigate to={destination} replace />;
  }
  return <>{children}</>;
}
